import React, { useRef, useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperType } from 'swiper';
import { Navigation, Pagination, A11y } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import * as Styled from './OCardCarouselCombo.styles';
import { OCardCarouselComboProps } from './OCardCarouselCombo.types';
import { MContentHeader } from '../../molecules/MContentHeader/index';
import { MCarouselControls } from '../../molecules/MCarouselControls/index';
import { MSlot } from '../../molecules/MSlot/index';
import {
  MContentHeaderAlignment,
  MContentHeaderAllowedButtonType,
} from '../../molecules/MContentHeader/MContentHeader.types';
import { MCarouselControlsType } from '../../molecules/MCarouselControls/MCarouselControls.types';
import { ViewportPx } from '../../utils/ImageUtils';

const COMPONENT_NAME = 'o-mi-web-ui-kit-OCardCarouselCombo';
const TEST_ID = 'mi-web-ui-kit-OCardCarouselCombo';

const normalizeHeaderAlignment = (alignment?: string): MContentHeaderAlignment => {
  const normalized = alignment?.trim().toLowerCase();
  if (normalized === 'center' || normalized === 'centered') {
    return MContentHeaderAlignment.Centered;
  }
  return MContentHeaderAlignment.Left;
};

export const OCardCarouselCombo = (props: OCardCarouselComboProps) => {
  const swiperRef = useRef<{ swiper: SwiperType } | null>(null);
  const [totalSlides, setTotalSlides] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);

  const {
    peeking: peekingProp,
    mContentHeader: mContentHeaderProp,
    mCarouselControls: mCarouselControlsProp,
    pagePath: pagePathProp,
    itemPath: itemPathProp,
    isAuthorMode = false,
    ':items': itemsProp,
    cqItems: cqItemsProp,
    aemReactComponentMapping = {},
    ':itemsOrder': itemsOrderProp,
    className = '',
  } = props;

  // In AEM, EditableComponent passes the model JSON nested under `props.model`,
  // whereas Storybook spreads the same fields at the top level. Resolve each
  // field from the top-level prop first, then fall back to `props.model`.
  const model = (props.model || {}) as Record<string, unknown>;
  const peeking = (peekingProp ?? (model['peeking'] as boolean | undefined) ?? false) as boolean;
  const mContentHeader = mContentHeaderProp ?? (model['mContentHeader'] as typeof mContentHeaderProp);
  const mCarouselControls = mCarouselControlsProp ?? (model['mCarouselControls'] as typeof mCarouselControlsProp);
  const pagePath = pagePathProp || (model['pagePath'] as string | undefined) || '';
  const itemPath = itemPathProp || (model['itemPath'] as string | undefined) || '';
  const items = (itemsProp ??
    (model[':items'] as typeof itemsProp) ??
    cqItemsProp ??
    (model['cqItems'] as typeof cqItemsProp) ??
    {}) as NonNullable<typeof itemsProp>;
  const itemsOrder = (itemsOrderProp ??
    (model[':itemsOrder'] as string[] | undefined) ??
    ((props as Record<string, unknown>)['cqItemsOrder'] as string[] | undefined) ??
    (model['cqItemsOrder'] as string[] | undefined) ??
    []) as string[];

  const handleSlideChange = (swiper: SwiperType) => {
    setCurrentSlide(swiper.snapIndex);
  };

  const updatePaginationState = (swiper: SwiperType) => {
    const nextTotalSlides = Math.max(swiper.snapGrid.length, 1);
    setTotalSlides(prev => (prev === nextTotalSlides ? prev : nextTotalSlides));
    setCurrentSlide(prev => (prev === swiper.snapIndex ? prev : swiper.snapIndex));
  };

  const handlePrev = () => {
    swiperRef.current?.swiper.slidePrev();
  };

  const handleNext = () => {
    swiperRef.current?.swiper.slideNext();
  };

  const getSlidesPerView = () => {
    if (peeking) {
      return 'auto';
    }
    return 1;
  };

  const getSpaceBetween = () => {
    return 16;
  };

  const authoredItemKeys = itemsOrder.filter((key: string) => Boolean(items[key]));
  const trailingDropZoneKey = `item${authoredItemKeys.length}`;
  const showTrailingAuthorDropZone = isAuthorMode;
  const headerAlignment = normalizeHeaderAlignment(mContentHeader?.alignment);
  const normalizedControlType = String(mCarouselControls?.type || '').toLowerCase();
  const controlType =
    normalizedControlType === MCarouselControlsType.Numbers
      ? MCarouselControlsType.Numbers
      : MCarouselControlsType.Dots;

  useEffect(() => {
    const pageWcmMode = document.getElementsByTagName('html')[0];
    if (!pageWcmMode) return;

    const toggleAuthoringClass = () => {
      const swiperElement = (swiperRef.current?.swiper?.el as HTMLElement | undefined) ?? null;
      if (!swiperElement) return;
      if (!isAuthorMode) {
        swiperElement.classList.remove('glide__slides_authoring');
        return;
      }
      const currWcmMode = pageWcmMode.getAttribute('class');
      if (currWcmMode?.includes('Edit')) {
        swiperElement.classList.add('glide__slides_authoring');
      } else if (currWcmMode?.includes('Preview')) {
        swiperElement.classList.remove('glide__slides_authoring');
      }
    };

    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.attributeName === 'class') {
          toggleAuthoringClass();
        }
      });
    });

    observer.observe(pageWcmMode, { attributes: true });
    toggleAuthoringClass();

    return () => {
      observer.disconnect();
    };
  }, [isAuthorMode]);

  useEffect(() => {
    if (swiperRef.current?.swiper) {
      updatePaginationState(swiperRef.current.swiper);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, peeking]);

  return (
    <Styled.Wrapper className={className} data-component-name={COMPONENT_NAME} data-testid={TEST_ID} $peeking={peeking}>
      <Styled.CarouselContainer>
        {mContentHeader && (
          <Styled.HeaderWrapper $alignment={headerAlignment}>
            <MContentHeader
              alignment={headerAlignment}
              mTextBlockSimple={mContentHeader.mTextBlockSimple}
              cta={mContentHeader.cta as 'true' | 'false' | undefined}
              mButton={
                mContentHeader.mButton
                  ? {
                      ...mContentHeader.mButton,
                      buttonType: mContentHeader.mButton.buttonType as MContentHeaderAllowedButtonType,
                      externalLink: mContentHeader.mButton.externalLink === 'true',
                      openInANewTab: mContentHeader.mButton.openInANewTab === 'true',
                    }
                  : undefined
              }
              secondaryCta={mContentHeader.secondaryCta as 'true' | 'false' | undefined}
              mButton2={
                mContentHeader.mButton2
                  ? {
                      ...mContentHeader.mButton2,
                      buttonType: mContentHeader.mButton2.buttonType as MContentHeaderAllowedButtonType,
                      externalLink: mContentHeader.mButton2.externalLink === 'true',
                      openInANewTab: mContentHeader.mButton2.openInANewTab === 'true',
                    }
                  : undefined
              }
            />
          </Styled.HeaderWrapper>
        )}

        <Styled.CarouselSection>
          <Styled.SwiperWrapper $peeking={peeking}>
            <Swiper
              ref={swiperRef}
              modules={[Navigation, Pagination, A11y]}
              spaceBetween={getSpaceBetween()}
              slidesPerView={getSlidesPerView()}
              onSwiper={(swiper: SwiperType) => {
                updatePaginationState(swiper);
              }}
              onSlideChange={(swiper: SwiperType) => {
                handleSlideChange(swiper);
                updatePaginationState(swiper);
              }}
              onBreakpoint={(swiper: SwiperType) => {
                updatePaginationState(swiper);
              }}
              breakpoints={{
                [ViewportPx.MD]: {
                  slidesPerView: 'auto',
                  spaceBetween: 24,
                },
                [ViewportPx.LG]: {
                  slidesPerView: peeking ? 'auto' : 3,
                  spaceBetween: 24,
                },
                [ViewportPx.XL]: {
                  slidesPerView: peeking ? 'auto' : 2,
                  spaceBetween: 24,
                },
              }}
            >
              {authoredItemKeys.map((key: string) => {
                const itemData = items[key];

                type AemChild = {
                  ':type'?: string;
                  ':items'?: Record<string, AemChild>;
                  ':itemsOrder'?: string[];
                };

                const childItem = itemData as AemChild;
                const childResourceType = (childItem[':type'] ?? (itemData as Record<string, unknown>)[':type']) as
                  | string
                  | undefined;
                const childItems = (childItem[':items'] ??
                  (itemData as Record<string, unknown>)['cqItems'] ??
                  {}) as Record<string, AemChild>;
                const childItemsOrder = (childItem[':itemsOrder'] ??
                  (itemData as Record<string, unknown>)['cqItemsOrder'] ??
                  []) as string[];
                const hasNestedItems = Object.keys(childItems).length > 0;
                const DirectChildComponent = childResourceType
                  ? aemReactComponentMapping[childResourceType]
                  : undefined;
                const shouldRenderDirectChild = !hasNestedItems && Boolean(DirectChildComponent);

                return (
                  <SwiperSlide key={key}>
                    <Styled.SlideContent $peeking={peeking}>
                      {shouldRenderDirectChild && DirectChildComponent ? (
                        <DirectChildComponent
                          model={itemData as Record<string, unknown>}
                          {...(itemData as Record<string, unknown>)}
                        />
                      ) : (
                        <MSlot
                          pagePath={pagePath}
                          itemPath={`${itemPath}/${key}`}
                          isAuthorMode={isAuthorMode}
                          cqItems={childItems}
                          model={{
                            ...(model as Record<string, unknown>),
                            ':order': childItemsOrder,
                            cqItems: items,
                            cqItemsOrder: itemsOrder,
                            cqType: (model as Record<string, unknown>)[':type'],
                            itemPath,
                            pagePath,
                          }}
                          aemReactComponentMapping={aemReactComponentMapping}
                        />
                      )}
                    </Styled.SlideContent>
                  </SwiperSlide>
                );
              })}
              {showTrailingAuthorDropZone && (
                <SwiperSlide key={trailingDropZoneKey}>
                  <Styled.SlideContent $peeking={peeking}>
                    <MSlot
                      pagePath={pagePath}
                      itemPath={`${itemPath}/${trailingDropZoneKey}`}
                      isAuthorMode={isAuthorMode}
                      cqItems={{}}
                      aemReactComponentMapping={aemReactComponentMapping}
                    />
                  </Styled.SlideContent>
                </SwiperSlide>
              )}
            </Swiper>
          </Styled.SwiperWrapper>

          {mCarouselControls && totalSlides > 1 && (
            <Styled.ControlsWrapper $imageOverlay={mCarouselControls.imageOverlay === 'true'}>
              <MCarouselControls
                type={controlType}
                imageOverlay={mCarouselControls.imageOverlay === 'true'}
                totalSlides={totalSlides}
                currentSlide={currentSlide}
                onPrev={handlePrev}
                onNext={handleNext}
              />
            </Styled.ControlsWrapper>
          )}
        </Styled.CarouselSection>
      </Styled.CarouselContainer>
    </Styled.Wrapper>
  );
};
